import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, ArrowRight, ArrowLeft, Loader2, Check, Mail, Calendar,
  MessageSquare, Database, FileText, Mic, ShieldCheck,
} from "lucide-react";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { openOAuthTab } from "@/lib/oauth";
import { toast } from "sonner";

const NAME_SUGGESTIONS = ["Ace", "Coach", "Champ", "Scout", "Buddy", "Mac", "Remy", "Goose"];
const STARTER_PROMPTS = [
  "Brief my day",
  "Draft a reply to my latest email",
  "Prep me for my next meeting",
  "Summarize what's on my plate this week",
];
const SURFACES = [
  { icon: MessageSquare, title: "Chat", desc: "Think out loud — get drafts, never auto-sends." },
  { icon: Mail, title: "Inbox", desc: "Your email & calendar, read in one calm place." },
  { icon: Calendar, title: "Meetings", desc: "Live notes, summaries and action items." },
  { icon: Database, title: "Vault", desc: "Your second brain — notes you fully own." },
  { icon: FileText, title: "Studio", desc: "Draft branded docs & decks in seconds." },
];

const labelStyle = { display: "block", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-dim)", marginBottom: 6 };

export default function OnboardingWizard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [name, setName] = useState(user?.name || "");
  const [title, setTitle] = useState(user?.title || "");
  const [assistant, setAssistant] = useState("");
  const [msConnected, setMsConnected] = useState(false);
  const [msEmail, setMsEmail] = useState("");
  const [msBusy, setMsBusy] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const pollRef = useRef(null);

  const firstName = (user?.name || "there").split(" ")[0];

  const loadMs = async () => {
    try {
      const r = await api.get("/integrations/microsoft/status");
      setMsConnected(!!r.data?.connected);
      setMsEmail(r.data?.email || "");
    } catch { /* ignore */ }
  };
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/integrations/microsoft/status");
        setMsConnected(!!r.data?.connected);
        setMsEmail(r.data?.email || "");
      } catch { /* ignore */ }
    })();
  }, []);

  // While on the connect step, poll so the new-tab OAuth return is reflected here.
  useEffect(() => {
    if (step !== 2) return undefined;
    pollRef.current = setInterval(loadMs, 3000);
    return () => clearInterval(pollRef.current);
  }, [step]);

  const connectMicrosoft = async () => {
    setMsBusy(true);
    const tab = openOAuthTab();
    try {
      const r = await api.get("/integrations/microsoft/connect");
      tab.go(r.data.authorize_url);
    } catch (e) {
      tab.fail();
      toast.error(e?.response?.data?.detail || "Microsoft 365 isn't ready yet. You can connect later in Settings.");
    } finally {
      setMsBusy(false);
    }
  };

  const finish = async (starterPrompt) => {
    if (finishing) return;
    setFinishing(true);
    try {
      const r = await api.post("/onboarding/complete", {
        assistant_name: assistant.trim(),
        title: title.trim(),
        name: name.trim(),
      });
      setUser(r.data);
      toast.success(`You're all set. Meet ${r.data.assistant_name}.`);
      navigate("/chat", starterPrompt ? { state: { starter: starterPrompt } } : undefined);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't finish setup. Please try again.");
      setFinishing(false);
    }
  };

  const canNext = step !== 1 || assistant.trim().length > 0;
  const TOTAL = 4;

  return (
    <div data-testid="onboarding-wizard" style={{
      position: "fixed", inset: 0, zIndex: 150, display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(12,12,16,0.72)", backdropFilter: "blur(6px)", padding: 24,
    }}>
      <div className="fade-up" style={{
        width: "100%", maxWidth: 580, background: "var(--panel)", border: "1px solid var(--hairline)",
        borderRadius: 18, boxShadow: "0 28px 70px rgba(0,0,0,0.45)", overflow: "hidden",
        display: "flex", flexDirection: "column", maxHeight: "92vh",
      }}>
        {/* header */}
        <div style={{ padding: "22px 28px 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Logo height={30} />
          <div style={{ display: "flex", gap: 6 }} data-testid="onboarding-progress">
            {Array.from({ length: TOTAL }).map((_, i) => (
              <span key={i} style={{
                width: i === step ? 22 : 7, height: 7, borderRadius: 50,
                background: i <= step ? "var(--ph-product-teal)" : "var(--hairline)",
                transition: "width 0.25s ease, background 0.25s ease",
              }} />
            ))}
          </div>
        </div>

        {/* body */}
        <div style={{ padding: "22px 28px 8px", overflowY: "auto", flex: 1 }}>
          {step === 0 && (
            <div data-testid="onboarding-step-welcome">
              <Eyebrow icon={Sparkles} label="Welcome" />
              <h2 className="ph-h2" style={{ marginBottom: 8 }}>Welcome, {firstName}.</h2>
              <p className="text-soft" style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 22 }}>
                Let&apos;s spend a minute making this yours. First, tell me a little about you.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <label style={labelStyle}>Your name</label>
                  <input className="field" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name" data-testid="onboarding-name" />
                </div>
                <div>
                  <label style={labelStyle}>Your role at Players Health</label>
                  <input className="field" value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Claims Analyst" data-testid="onboarding-title" />
                  <div className="text-dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                    Helps tailor briefings and drafts to your work.
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="onboarding-step-assistant">
              <Eyebrow icon={Sparkles} label="Your assistant" />
              <h2 className="ph-h2" style={{ marginBottom: 8 }}>Name your assistant</h2>
              <p className="text-soft" style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 18 }}>
                This is who you&apos;ll talk to all day — and the name you&apos;ll say out loud to go hands-free.
                Make it yours.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }} data-testid="onboarding-name-suggestions">
                {NAME_SUGGESTIONS.map((n) => (
                  <button key={n} className="chip" onClick={() => setAssistant(n)} data-testid={`onboarding-suggest-${n}`}
                    style={{
                      borderColor: assistant === n ? "var(--ph-product-teal)" : undefined,
                      color: assistant === n ? "var(--ph-product-teal)" : undefined,
                    }}>
                    {n}
                  </button>
                ))}
              </div>
              <label style={labelStyle}>Assistant name</label>
              <input className="field" value={assistant} onChange={(e) => setAssistant(e.target.value)}
                placeholder="Type a name…" data-testid="onboarding-assistant-name" maxLength={30} autoFocus />
              {assistant.trim() && (
                <div className="text-dim" style={{ fontSize: 12, marginTop: 10, display: "flex", alignItems: "center", gap: 7 }}>
                  <Mic size={13} /> Wake phrase will be <strong style={{ color: "var(--ink-soft)" }}>&ldquo;Hey {assistant.trim()}&rdquo;</strong>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-connect">
              <Eyebrow icon={Mail} label="Connect" />
              <h2 className="ph-h2" style={{ marginBottom: 8 }}>Connect email &amp; calendar</h2>
              <p className="text-soft" style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
                Link Microsoft 365 so {assistant.trim() || "your assistant"} can brief your day, prep your meetings,
                and draft replies. <strong style={{ color: "var(--ink-soft)" }}>Read-only and drafts-only</strong> — nothing is ever sent without you.
              </p>
              {msConnected ? (
                <div data-testid="onboarding-ms-connected" style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "16px 18px", borderRadius: 12,
                  background: "rgba(78,205,196,0.12)", border: "1px solid rgba(78,205,196,0.4)",
                }}>
                  <ShieldCheck size={20} style={{ color: "var(--ph-product-teal)" }} />
                  <div>
                    <div style={{ fontSize: 14, color: "var(--ink)" }}>Microsoft 365 connected</div>
                    {msEmail && <div className="text-dim" style={{ fontSize: 12.5 }}>{msEmail}</div>}
                  </div>
                </div>
              ) : (
                <>
                  <button className="btn btn-primary" onClick={connectMicrosoft} disabled={msBusy} data-testid="onboarding-connect-ms"
                    style={{ width: "100%", justifyContent: "center", padding: "13px 16px" }}>
                    {msBusy ? <Loader2 size={16} className="spin" /> : (
                      <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
                        <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                        <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                        <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                        <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
                      </svg>
                    )}
                    Connect Microsoft 365
                  </button>
                  <div className="text-dim" style={{ fontSize: 12, marginTop: 12, textAlign: "center" }}>
                    A new tab will open to sign in. You can also do this later in Settings.
                  </div>
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-tips">
              <Eyebrow icon={Check} label="You're set" />
              <h2 className="ph-h2" style={{ marginBottom: 8 }}>Try {assistant.trim() || "your assistant"} now</h2>
              <p className="text-soft" style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
                Tap a starter prompt to jump in — or just start typing in Chat.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 22 }} data-testid="onboarding-starters">
                {STARTER_PROMPTS.map((p, i) => (
                  <button key={p} onClick={() => finish(p)} disabled={finishing} data-testid={`onboarding-starter-${i}`}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                      padding: "13px 16px", borderRadius: 10, background: "var(--panel-2)",
                      border: "1px solid var(--hairline)", cursor: "pointer", textAlign: "left",
                      color: "var(--ink)", fontSize: 14, transition: "border-color 0.15s ease",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--ph-product-teal)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--hairline)"; }}>
                    <span>{p}</span>
                    <ArrowRight size={15} style={{ color: "var(--ink-dim)", flexShrink: 0 }} />
                  </button>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {SURFACES.map((s) => (
                  <div key={s.title} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", borderRadius: 10, background: "var(--panel-2)" }}>
                    <s.icon size={16} style={{ color: "var(--ph-brand-violet)", flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <div style={{ fontSize: 13, color: "var(--ink)" }}>{s.title}</div>
                      <div className="text-dim" style={{ fontSize: 11.5, lineHeight: 1.4 }}>{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* footer */}
        <div style={{
          padding: "16px 28px 22px", display: "flex", alignItems: "center", justifyContent: "space-between",
          borderTop: "1px solid var(--hairline-soft)", gap: 12,
        }}>
          <button className="btn btn-ghost" onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0} data-testid="onboarding-back"
            style={{ opacity: step === 0 ? 0.35 : 1, padding: "10px 14px" }}>
            <ArrowLeft size={15} /> Back
          </button>

          {step < TOTAL - 1 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {step === 2 && !msConnected && (
                <button className="btn btn-ghost" onClick={() => setStep(3)} data-testid="onboarding-skip-connect" style={{ padding: "10px 14px" }}>
                  Skip for now
                </button>
              )}
              <button className="btn btn-primary" onClick={() => setStep((s) => s + 1)} disabled={!canNext}
                data-testid="onboarding-next" style={{ padding: "10px 18px" }}>
                {step === 2 && msConnected ? "Continue" : "Next"} <ArrowRight size={15} />
              </button>
            </div>
          ) : (
            <button className="btn btn-primary" onClick={() => finish(null)} disabled={finishing}
              data-testid="onboarding-finish" style={{ padding: "10px 20px" }}>
              {finishing ? <Loader2 size={15} className="spin" /> : <Check size={15} />}
              {finishing ? "Setting up…" : "Get started"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Eyebrow({ icon: Icon, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
      <Icon size={15} style={{ color: "var(--ph-brand-violet)" }} />
      <span style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-dim)" }}>{label}</span>
    </div>
  );
}
